import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DnaBackground } from "@/components/DnaBackground";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { useTranslation } from "react-i18next";
import { authAPI } from "@/lib/api";
import { calculatePasswordStrength, getPasswordRequirements } from "@/lib/password-validation";
import { toast } from "sonner";
import { Lock, CheckCircle2, Loader2 } from "lucide-react";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetToken } = useParams<{ resetToken: string }>();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Password strength calculation
  const passwordStrength = calculatePasswordStrength(password, t);
  const passwordRequirements = getPasswordRequirements(password, t);

  const resetPasswordMutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authAPI.resetPassword(token, password),
    onSuccess: () => {
      toast.success(
        t('resetPassword.success') || 
        'Password reset successfully! You can now sign in with your new password.'
      );
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsMismatch') || 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error(t('auth.passwordTooShort') || 'Password must be at least 6 characters');
      return;
    }

    if (!/[A-Z]/.test(password)) {
      toast.error(
        t('auth.passwordNeedsUppercase') || 
        'Password must contain at least one uppercase letter'
      );
      return;
    }

    if (!/\d/.test(password)) {
      toast.error(t('auth.passwordNeedsNumber') || 'Password must contain at least one number');
      return;
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      toast.error(
        t('auth.passwordNeedsSpecial') || 
        'Password must contain at least one special character'
      );
      return;
    }

    if (!resetToken) {
      toast.error(t('resetPassword.invalidToken') || 'Invalid reset token');
      return;
    }

    resetPasswordMutation.mutate({ token: resetToken, password });
  };

  if (resetPasswordMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 relative">
        <DnaBackground />
        
        <Card className="w-full max-w-md p-8 bg-card border-border shadow-glow animate-scale-in">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-3xl font-bold text-gradient mb-4">
              {t('resetPassword.successTitle') || 'Password Reset!'}
            </h1>
            
            <p className="text-muted-foreground mb-6">
              {t('resetPassword.successMessage') || 
                'Your password has been successfully reset. Redirecting to sign in...'}
            </p>

            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-primary hover:bg-primary/90 shadow-glow"
            >
              {t('forgotPassword.backToSignIn') || 'Go to Sign In'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <DnaBackground />
      
      <Card className="w-full max-w-md p-8 bg-card border-border shadow-glow animate-scale-in">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          
          <h1 className="text-3xl font-bold text-gradient mb-2">
            {t('resetPassword.title') || 'Reset Password'}
          </h1>
          
          <p className="text-muted-foreground">
            {t('resetPassword.description') || 
              'Please enter your new password below.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordInput
            id="password"
            label={t('resetPassword.newPassword') || 'New Password'}
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />

          {password && (
            <PasswordStrengthIndicator
              strength={passwordStrength}
              requirements={passwordRequirements}
              strengthLabel={t('auth.passwordStrength') || 'Password Strength'}
            />
          )}

          <PasswordInput
            id="confirmPassword"
            label={t('auth.confirmPassword') || 'Confirm Password'}
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />

          {resetPasswordMutation.isError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md text-start">
              {getLocalizedErrorMessage(resetPasswordMutation.error, "errors.passwordResetFailed")}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 shadow-glow"
            disabled={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading') || 'Resetting...'}
              </>
            ) : (
              t('resetPassword.resetButton') || 'Reset Password'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate('/auth')}
            className="text-sm text-primary hover:underline"
          >
            {t('forgotPassword.backToSignIn') || 'Back to Sign In'}
          </button>
        </div>
      </Card>
    </div>
  );
}
